export var UserRole;
(function (UserRole) {
    UserRole["Admin"] = "admin";
    UserRole["Executive"] = "executive";
    UserRole["AccountingManager"] = "accounting_manager";
    UserRole["GeneralManager"] = "general_manager";
})(UserRole || (UserRole = {}));
export var PaySheetState;
(function (PaySheetState) {
    PaySheetState["InProgress"] = "in_progress";
    PaySheetState["Completed"] = "completed";
})(PaySheetState || (PaySheetState = {}));
export var GrossEntryMode;
(function (GrossEntryMode) {
    GrossEntryMode["Shared"] = "shared";
    /**
     * Like Shared, but the department gross is first reduced by the plan's
     * total commission pool (every assigned manager's rate + met kickers)
     * before commissions are computed. See computeAdjustedGross (backend).
     */
    GrossEntryMode["SharedAdjusted"] = "shared_adjusted";
    GrossEntryMode["Independent"] = "independent";
    GrossEntryMode["None"] = "none";
})(GrossEntryMode || (GrossEntryMode = {}));
/**
 * True for the modes that share one department-level gross entry across all
 * employees on the plan (Shared and SharedAdjusted). Use this instead of
 * `=== GrossEntryMode.Shared` wherever "shared" behavior is meant.
 */
export function isSharedGrossMode(mode) {
    return mode === GrossEntryMode.Shared || mode === GrossEntryMode.SharedAdjusted;
}
export const DEPARTMENTS = ['Sales', 'Service', 'Parts', 'Office'];
/**
 * Canonical list of Darling's rooftops. These strings must match the
 * `companyName` attribute on the corresponding Entra ID user profiles
 * byte-for-byte — the authorizer and scope checks key off this value.
 *
 * "Corporate" is the HQ entry used by Admins and Executives. They bypass
 * rooftop scope enforcement, so the value here only needs to satisfy the
 * provisioning allowlist; it is never used as a filter key.
 */
export const LOCATIONS = [
    'Corporate',
    'Honda',
    'Ford',
    'Kia',
    'Augusta',
    'Automall',
    'Chevy',
    'Newport',
    'Brunswick',
    'Downeast Outlet',
    'Greenpoint',
    'CVC',
];
/**
 * Ingress-boundary aliases for Entra companyName values that don't match
 * a canonical LOCATIONS entry. ONLY applied to read-side / display flows:
 *   - Graph roster queries (listUsersByLocation filter fan-out)
 *   - Backend employees.ts sync (writes to the employees table)
 *
 * NEVER apply to provision-user for the General Manager role. Reason: a
 * VC-tagged GM would silently become a Ford GM (cross-rooftop escalation).
 * See provision-user.ts — scope-bearing roles keep strict LOCATIONS check.
 *
 * Assumes the Entra tenant admin controls which companyName values are
 * issued. If Entra self-service ever lets users edit their own
 * companyName, this aliasing becomes a privilege-escalation vector.
 */
export const LOCATION_ALIASES = {
    'Value Center': 'Ford',
    VC: 'Ford',
};
export function canonicalLocation(raw) {
    const trimmed = (raw ?? '').trim();
    return LOCATION_ALIASES[trimmed] ?? trimmed;
}
/**
 * Reverse map for Graph filter fan-out: for canonical 'Ford', returns
 * ['Ford', 'Value Center', 'VC'] so listUsersByLocation can query all
 * aliases that resolve to Ford in a single OR-expression.
 */
export function aliasesForCanonical(canonical) {
    const reversed = Object.entries(LOCATION_ALIASES)
        .filter(([, v]) => v === canonical)
        .map(([k]) => k);
    return [canonical, ...reversed];
}
/**
 * Executive-level roles: company-wide scope AND full write access to everything
 * except pay-plan templates. Mirrors the backend `EXEC_AND_ACCOUNTING` route
 * tier. General Managers are excluded — they are read-only for their rooftop.
 * Use this for both the company-wide rooftop selectors and write-action gating
 * (the two populations coincide after GM became read-only).
 */
export function isExecLevel(role) {
    return (role === UserRole.Admin || role === UserRole.Executive || role === UserRole.AccountingManager);
}
/**
 * Roles allowed to create, modify, or delete pay-plan templates. Mirrors the
 * backend `EXEC_ONLY` tier — Accounting Managers are deliberately excluded.
 */
export function isTemplateAdmin(role) {
    return role === UserRole.Admin || role === UserRole.Executive;
}
export const ENTRA_GROUP_ROLE_MAP = {
    '44da8ce4-058d-450c-9175-d1277f5694a4': UserRole.Admin,
    'ebf208a7-94b0-471e-85ab-c119b1e295fd': UserRole.Executive,
    '546da5af-13bd-437d-a19c-35bc6b4af426': UserRole.AccountingManager,
    '60f1a173-d72d-428e-be9c-a04572449189': UserRole.GeneralManager,
};
//# sourceMappingURL=index.js.map